import classnames from 'classnames';
import * as React from 'react';

import { IGhMediaGalleryViewProps } from './gh-media-gallery';

// React 16 passes unknown attributes through; typing needs the cast.
const HIGH_PRIORITY = ({ fetchpriority: 'high' } as unknown) as React.ImgHTMLAttributes<HTMLImageElement>;

const addTo = (set: ReadonlySet<number>, index: number): ReadonlySet<number> => (set.has(index) ? set : new Set(set).add(index));

const GhMediaGalleryUI: React.FC<IGhMediaGalleryViewProps> = ({ images, enableZoom, className, resources }) => {
    const [activeIndex, setActiveIndex] = React.useState(0);
    // Stage and thumbnail variants are different downloads, so they are tracked
    // separately - a loaded thumbnail says nothing about the large image.
    const [stageLoaded, setStageLoaded] = React.useState<ReadonlySet<number>>(new Set<number>());
    const [thumbLoaded, setThumbLoaded] = React.useState<ReadonlySet<number>>(new Set<number>());
    const [failed, setFailed] = React.useState<ReadonlySet<number>>(new Set<number>());
    const [zoomRequested, setZoomRequested] = React.useState(false);
    const [zoomOrigin, setZoomOrigin] = React.useState('50% 50%');
    const rootRef = React.useRef<HTMLDivElement>(null);
    const displayedRef = React.useRef<number | null>(null);
    const prefetchedRef = React.useRef<Set<number>>(new Set<number>());

    // Native listeners rather than React's onLoad/onError: those events don't
    // bubble, so a server-rendered image completing around hydration can be
    // missed. Images that are already complete are resolved on the spot.
    React.useEffect(() => {
        const elements = Array.from(rootRef.current?.querySelectorAll<HTMLImageElement>('img[data-gallery-role]') ?? []);
        const cleanups: (() => void)[] = [];

        elements.forEach(element => {
            const index = Number(element.dataset.galleryIndex);
            const isStage = element.dataset.galleryRole === 'stage';
            if (Number.isNaN(index)) {
                return;
            }
            const onLoad = () => (isStage ? setStageLoaded(prev => addTo(prev, index)) : setThumbLoaded(prev => addTo(prev, index)));
            const onError = () => setFailed(prev => addTo(prev, index));

            if (element.complete) {
                if (element.naturalWidth > 0) {
                    onLoad();
                } else {
                    onError();
                }
                return;
            }
            element.addEventListener('load', onLoad);
            element.addEventListener('error', onError);
            cleanups.push(() => {
                element.removeEventListener('load', onLoad);
                element.removeEventListener('error', onError);
            });
        });

        return () => cleanups.forEach(cleanup => cleanup());
    }, [images, activeIndex]);

    React.useEffect(() => {
        if (stageLoaded.has(activeIndex)) {
            displayedRef.current = activeIndex;
        }
    }, [stageLoaded, activeIndex]);

    // A missing image must never leave the stage empty - fall forward to the next candidate.
    React.useEffect(() => {
        if (!failed.has(activeIndex)) {
            return;
        }
        const next = images.findIndex((_image, index) => !failed.has(index));
        if (next >= 0 && next !== activeIndex) {
            setActiveIndex(next);
        }
    }, [failed, activeIndex, images]);

    if (images.length === 0) {
        return null;
    }

    const active = images[activeIndex];
    const isActiveReady = stageLoaded.has(activeIndex) || failed.size === images.length;
    // The browser keeps painting the previous image until the new one decodes, so
    // without this the gallery looks frozen while a cold variant downloads.
    const isSwitching = !stageLoaded.has(activeIndex) && displayedRef.current !== null && displayedRef.current !== activeIndex;

    // Warm the stage variant on intent, so most clicks resolve instantly.
    const prefetch = (index: number) => {
        const image = images[index];
        if (!image || prefetchedRef.current.has(index)) {
            return;
        }
        prefetchedRef.current.add(index);
        const warm = new Image();
        warm.src = image.src;
    };

    const handleZoomMove = (event: React.MouseEvent<HTMLDivElement>) => {
        const bounds = event.currentTarget.getBoundingClientRect();
        const x = ((event.clientX - bounds.left) / bounds.width) * 100;
        const y = ((event.clientY - bounds.top) / bounds.height) * 100;
        setZoomOrigin(`${x}% ${y}%`);
    };

    return (
        <div
            className={classnames('gh-media-gallery', { 'gh-media-gallery--has-thumbs': images.length > 1 }, className)}
            aria-label={resources.galleryAriaLabel}
            ref={rootRef}
        >
            <div
                className={classnames('gh-media-gallery__stage', {
                    'is-ready': isActiveReady,
                    'is-switching': isSwitching,
                    'is-zoomable': enableZoom
                })}
                onMouseEnter={enableZoom ? () => setZoomRequested(true) : undefined}
                onMouseMove={enableZoom ? handleZoomMove : undefined}
            >
                {active && (
                    <img
                        {...HIGH_PRIORITY}
                        className='gh-media-gallery__image'
                        data-gallery-role='stage'
                        data-gallery-index={activeIndex}
                        src={active.src}
                        alt={active.altText}
                        decoding='async'
                    />
                )}
                {enableZoom && zoomRequested && active && (
                    <img
                        className='gh-media-gallery__zoom'
                        src={active.zoomSrc}
                        alt=''
                        aria-hidden='true'
                        decoding='async'
                        style={{ transformOrigin: zoomOrigin }}
                    />
                )}
                {isSwitching && <span className='gh-media-gallery__spinner' aria-hidden='true' />}
            </div>

            {images.length > 1 && (
                <ul className='gh-media-gallery__thumbs' aria-label={resources.thumbnailListAriaLabel}>
                    {images.map((image, index) =>
                        failed.has(index) ? null : (
                            <li className='gh-media-gallery__thumb-item' key={image.key}>
                                <button
                                    type='button'
                                    className={classnames('gh-media-gallery__thumb', {
                                        'is-active': index === activeIndex,
                                        'is-loaded': thumbLoaded.has(index)
                                    })}
                                    aria-label={resources.thumbnailAriaLabel.replace('{0}', `${index + 1}`)}
                                    aria-current={index === activeIndex}
                                    onClick={() => setActiveIndex(index)}
                                    onMouseEnter={() => prefetch(index)}
                                    onFocus={() => prefetch(index)}
                                >
                                    <img
                                        className='gh-media-gallery__thumb-image'
                                        data-gallery-role='thumb'
                                        data-gallery-index={index}
                                        src={image.thumbSrc}
                                        alt=''
                                        decoding='async'
                                    />
                                </button>
                            </li>
                        )
                    )}
                </ul>
            )}
        </div>
    );
};

export default (props: IGhMediaGalleryViewProps) => <GhMediaGalleryUI {...props} />;
